import { motion, AnimatePresence } from "framer-motion";
import { Sliders, Sparkles } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { Switch } from "./Switch";
import { Slider } from "./Slider";
import { itemVariants } from "./motion";
import type { AiResponseSettings, ReplyLength, Tone } from "../mockAutomations";

interface AiResponseSettingsCardProps {
  settings: AiResponseSettings;
  onChange: (patch: Partial<AiResponseSettings>) => void;
}

const LANGUAGES = ["English", "Bengali", "Arabic", "Hindi", "Urdu", "Spanish"];

export function AiResponseSettingsCard({ settings, onChange }: AiResponseSettingsCardProps) {
  return (
    <motion.div variants={itemVariants} className="h-full">
      <Card className="max-w-none h-full overflow-hidden rounded-2xl border border-slate-200/90 dark:border-slate-800 bg-slate-50/40 dark:bg-slate-900/40 shadow-xs flex flex-col">
        {/* Pod Header */}
        <div className="flex flex-wrap items-center justify-between border-b border-slate-200/70 dark:border-slate-800/80 bg-slate-100/50 dark:bg-slate-850/60 px-4 py-2.5 gap-2">
          <div className="flex items-center gap-2">
            <span className="flex h-5 w-5 items-center justify-center rounded-lg bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">
              <Sparkles className="h-3 w-3" />
            </span>
            <span className="text-[11px] font-bold tracking-wider uppercase text-slate-800 dark:text-slate-200">
              AI Response Engine
            </span>
            <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200/80 dark:border-emerald-900/60 bg-emerald-50 dark:bg-emerald-950/40 px-2 py-0.2 text-[9px] font-bold text-emerald-700 dark:text-emerald-400">
              {settings.autoReply ? "Auto-Pilot" : "Drafting"}
            </span>
          </div>
          <span className="text-[10px] font-medium text-slate-400 dark:text-slate-500">Persona & fallback rules</span>
        </div>

        <div className="p-3.5 bg-white/70 dark:bg-slate-900/70 space-y-3 flex-1">
          {/* Main Auto-reply toggle capsule */}
          <div className="flex items-center justify-between rounded-xl border border-emerald-200/80 dark:border-emerald-900/60 bg-emerald-50/40 dark:bg-emerald-950/30 px-3 py-2">
            <div>
              <div className="text-xs font-bold text-slate-900 dark:text-slate-100">Auto-reply to customers</div>
              <p className="text-[11px] text-slate-500 dark:text-slate-400">
                {settings.autoReply ? "AI sends replies immediately in active channels." : "AI drafts replies in inbox for human review."}
              </p>
            </div>
            <Switch
              checked={settings.autoReply}
              onCheckedChange={(checked) => onChange({ autoReply: checked })}
              aria-label="Auto-reply to customers"
            />
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <Label htmlFor="ai-tone" className="text-[11px] font-bold uppercase tracking-wider text-slate-600 dark:text-slate-400">Tone / Persona</Label>
              <Select
                id="ai-tone"
                value={settings.tone}
                onChange={(e) => onChange({ tone: e.target.value as Tone })}
                className="h-8.5 rounded-xl border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 text-xs"
              >
                <option value="friendly">Friendly</option>
                <option value="professional">Professional</option>
                <option value="casual">Casual</option>
              </Select>
            </div>
            <div className="space-y-1">
              <Label htmlFor="ai-reply-length" className="text-[11px] font-bold uppercase tracking-wider text-slate-600 dark:text-slate-400">Reply Length</Label>
              <Select
                id="ai-reply-length"
                value={settings.replyLength}
                onChange={(e) => onChange({ replyLength: e.target.value as ReplyLength })}
                className="h-8.5 rounded-xl border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 text-xs"
              >
                <option value="short">Short</option>
                <option value="medium">Medium</option>
                <option value="detailed">Detailed</option>
              </Select>
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-2 sm:items-end">
            <div className="space-y-1">
              <Label htmlFor="ai-language" className="text-[11px] font-bold uppercase tracking-wider text-slate-600 dark:text-slate-400">Default Language</Label>
              <Select
                id="ai-language"
                value={settings.language}
                disabled={settings.autoDetectLanguage}
                onChange={(e) => onChange({ language: e.target.value })}
                className="h-8.5 rounded-xl border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 text-xs"
              >
                {LANGUAGES.map((lang) => (
                  <option key={lang} value={lang}>{lang}</option>
                ))}
              </Select>
            </div>
            <label className="flex h-8.5 items-center justify-between gap-2.5 px-1 cursor-pointer">
              <span className="text-xs font-semibold text-slate-700 dark:text-slate-200">Auto-detect</span>
              <Switch
                checked={settings.autoDetectLanguage}
                onCheckedChange={(checked) => onChange({ autoDetectLanguage: checked })}
                aria-label="Auto-detect customer language"
              />
            </label>
          </div>

          <div className="flex items-center justify-between rounded-xl border border-slate-200/80 dark:border-slate-800 bg-white dark:bg-slate-850 p-2.5 shadow-2xs">
            <div>
              <div className="text-xs font-bold text-slate-800 dark:text-slate-200">Fall back to human when unsure</div>
              <p className="text-[10px] text-slate-400 dark:text-slate-500">Route low-confidence queries to operators.</p>
            </div>
            <Switch
              checked={settings.fallbackToHuman}
              onCheckedChange={(checked) => onChange({ fallbackToHuman: checked })}
              aria-label="Fall back to human when unsure"
            />
          </div>

          <AnimatePresence initial={false}>
            {settings.fallbackToHuman && (
              <motion.div
                key="confidence"
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="overflow-hidden"
              >
                <div className="space-y-1.5 rounded-xl border border-slate-200/70 dark:border-slate-800 bg-slate-50 dark:bg-slate-850 p-2.5">
                  <div className="flex items-center justify-between text-xs">
                    <span className="font-semibold text-slate-700 dark:text-slate-300">Confidence Threshold</span>
                    <span className="font-bold text-emerald-600 dark:text-emerald-400">{settings.confidenceThreshold}%</span>
                  </div>
                  <Slider
                    value={settings.confidenceThreshold}
                    onChange={(v) => onChange({ confidenceThreshold: v })}
                    aria-label="Confidence threshold before escalating"
                  />
                  <p className="text-[10px] text-slate-400 dark:text-slate-500">
                    Below this confidence, the AI escalates to human instead of guessing.
                  </p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </Card>
    </motion.div>
  );
}
